import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Download, X, Globe2, Database, FileBox, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal, Overline, SectionShell } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { CATALOG_DATASETS, CATALOG_CATEGORIES, IMAGES } from "../content";

export default function DataCatalog() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = CATALOG_DATASETS.filter((d) => {
    const q = query.toLowerCase();
    const matchQ = !q || d.name.toLowerCase().includes(q) || d.desc.toLowerCase().includes(q);
    const matchC = cat === "All" || d.cat === cat;
    return matchQ && matchC;
  });

  return (
    <main data-testid="data-catalog-page">
      <PageHero
        overline="DATA CATALOG"
        title="Thousands of data signals."
        highlight="Instantly."
        desc="Connect to relevant external geospatial data — quality ingredients at scale, from global POI fabric to rooftop geocoding and hazard layers."
        image={IMAGES.satellite_matrix}
      />

      <SectionShell className="pt-4">
        <Reveal>
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center mb-10">
            <div className="relative flex-1 max-w-xl">
              <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search datasets — POI, geocoding, terrain, hazard…"
                data-testid="catalog-search-input"
                className="w-full bg-[#111622]/80 border border-white/10 rounded-full pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60 focus:shadow-[0_0_20px_rgba(0,240,255,0.1)] transition-all"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="catalog-category-filters">
              {CATALOG_CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`catalog-filter-${c.toLowerCase()}`}
                  className={`font-mono-tech text-[11px] tracking-wider uppercase px-4 py-2 rounded-full border transition-all duration-200 ${
                    cat === c
                      ? "bg-cyan-400 text-[#05070A] border-cyan-400 font-semibold"
                      : "text-slate-400 border-white/10 hover:border-cyan-400/40 hover:text-cyan-300"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="catalog-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((d) => (
              <motion.button
                layout
                key={d.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.3 }}
                onClick={() => setSelected(d)}
                data-testid={`dataset-card-${d.id}`}
                className="card-spotlight text-left border border-white/10 rounded-2xl bg-[#111622]/70 p-6 hover:border-cyan-500/50 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-3 py-1">
                    {d.cat}
                  </span>
                  <Database size={16} className="text-slate-600" />
                </div>
                <h3 className="font-display text-lg font-bold text-white">{d.name}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2">{d.desc}</p>
                <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-2 gap-2 font-mono-tech text-[10px] text-slate-500 uppercase tracking-wider">
                  <span>REC — <span className="text-slate-300">{d.records}</span></span>
                  <span>COV — <span className="text-slate-300">{d.coverage}</span></span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-slate-500 py-16 font-mono-tech text-sm" data-testid="catalog-empty-state">
            No datasets match — try a different signal.
          </p>
        )}
      </SectionShell>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#05070A]/80 backdrop-blur-md"
            onClick={() => setSelected(null)}
            data-testid="dataset-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg bg-[#0B0E14] border border-white/15 rounded-3xl p-8"
              data-testid="dataset-modal"
            >
              <button
                onClick={() => setSelected(null)}
                data-testid="dataset-modal-close"
                className="absolute top-5 right-5 text-slate-500 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
              <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-cyan-400">{selected.cat} DATASET</span>
              <h3 className="font-display text-2xl font-extrabold text-white mt-2">{selected.name}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{selected.desc}</p>
              <div className="mt-6 space-y-3">
                {[[Globe2, "Coverage", selected.coverage], [Database, "Records", selected.records], [FileBox, "Formats", selected.fmt]].map(([Icon, k, v]) => (
                  <div key={k} className="flex items-center gap-3 text-sm">
                    <Icon size={16} className="text-cyan-400" />
                    <span className="text-slate-500 w-20">{k}</span>
                    <span className="text-slate-200 font-medium">{v}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  toast.success("Sample request logged — our team will share the extract shortly.");
                  setSelected(null);
                }}
                data-testid="dataset-sample-request-button"
                className="mt-8 w-full flex items-center justify-center gap-2 bg-cyan-400 text-[#05070A] font-semibold py-3.5 rounded-full hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.4)] transition-all"
              >
                <Download size={16} /> Request sample extract
              </button>
              <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                <CheckCircle2 size={13} className="text-cyan-500" /> Quality-verified. Delivery in compatible formats.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CtaSection />
    </main>
  );
}
